import { Component, Input, OnInit } from '@angular/core';
import { Directions, SimulatorConfig } from '../../core/models/simulation.model';
import { Destroyable } from '../../utils/mixins/destroyable.mixin';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SimulatorType } from '../../core/enums/simulator-type.enum';
import { Router } from '@angular/router';
import { SimulationsService } from '../../services/simulations/simulations.service';
import { SnackbarService } from '../../services/snackbar.service';
import { LoadingService } from '../../services/loading.service';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../core/consts/api.const';
import { MatDialog } from '@angular/material/dialog';
import { SelectRockFilePopupComponent } from '../popups/select-rock-file-popup/select-rock-file-popup.component';
import { filter } from 'rxjs';
import { RockFile } from '../../core/models/rock-files.model';
import { sanitizeFileName, sanitizeSimulationName } from '../../utils/sanitize.util';
import { RemoveExtensionPipe } from '../pipes/remove-extension.pipe';
import { RockThumbnailService } from '../../services/rock-thumbnail/rock-thumbnail.service';
import { RockImagesService } from '../../services/rock-images/rock-images.service';
import { RockFileLoader } from '../../utils/rock-file-loader.util';
import { RockPreviewComponent } from '../rock-preview/rock-preview.component';
import { MatCardModule } from '@angular/material/card';

@Component({
  selector: 'app-basic-simulator',
  imports: [
    MatCardModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSnackBarModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    RockPreviewComponent,
],
  templateUrl: './basic-simulator.component.html',
  providers:[RemoveExtensionPipe],
  styleUrl: './basic-simulator.component.scss',
  standalone: true
})
export class BasicSimulatorComponent extends Destroyable(Object) implements OnInit {
  @Input() config!: SimulatorConfig;

  simulationDirectionsForm!: FormGroup;
  simulationNameForm!: FormGroup;
  fileName!: FormControl;
  isLoading: boolean = false;
  thumbnailUrl: string | null = null;
  rockFileMetadata: RockFile | null = null;
  
  private rockFileLoader: RockFileLoader;

  constructor(
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly simulationsService: SimulationsService,
    private readonly snackbarService: SnackbarService,
    private readonly loadingService: LoadingService,
    private readonly removeExtensionPipe: RemoveExtensionPipe,
    private readonly rockThumbnailService: RockThumbnailService,
    private readonly rockImagesService: RockImagesService,
  ) {
    super();
    this.rockFileLoader = new RockFileLoader(rockThumbnailService, rockImagesService);
  }

  get totalPorosity(): string {
    const fractionHistogram = this.rockFileMetadata?.fractionHistogram;
    if (fractionHistogram && fractionHistogram['0'] != null) {
      const porosity = fractionHistogram['0'];
      return porosity.toFixed(2);
    }
    return 'N/A';
  }

  get componentId(): SimulatorType {
    return this.config.simulatorType;
  }

  get nameControl(): FormControl {
    return this.simulationNameForm.get('name') as FormControl;
  }

  ngOnInit(): void {
    this.initSimulationNameForm();
    this.initSimulationDirectionsForm();
    this.initFileName();
    this.subscribeToLoadingState();
  }

  selectRockFileName(): void {
    const dialogRef = this.dialog.open(SelectRockFilePopupComponent, {
      panelClass: 'large-popup-size'
    });

    dialogRef.afterClosed()
      .pipe(
        filter(Boolean),
        this.takeUntilDestroyed()
      )
      .subscribe((file: RockFile) => {
        this.fileName.setValue(this.removeExtensionPipe.transform(file.fileName));
        this.loadThumbnail(file.fileName);
      });
  }

  private loadThumbnail(fileName: string): void {
    const previousThumbnailUrl = this.thumbnailUrl;

    // Load thumbnail (always works)
    this.rockFileLoader
      .getThumbnail(fileName)
      .pipe(this.takeUntilDestroyed())
      .subscribe((url) => {
        if (url) {
          if (previousThumbnailUrl) {
            this.rockFileLoader.revokeThumbnailUrl(previousThumbnailUrl);
          }
          this.thumbnailUrl = url;
        }
      });

    this.rockFileLoader
      .getMetadata(fileName)
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (metadata) => {
          this.rockFileMetadata = metadata;
        },
        error: () => {
          // Metadata not available, porosity will show N/A
          this.rockFileMetadata = null;
        }
      });
  }

  override ngOnDestroy(): void {
    if (this.thumbnailUrl) {
      this.rockFileLoader.revokeThumbnailUrl(this.thumbnailUrl);
    }
    super.ngOnDestroy();
  }

  runSimulation(): void {
    if (this.isSimulationInputValid()) {
      const directions: Directions = this.simulationDirectionsForm.getRawValue();
      const options = {
        headers: new HttpHeaders({
          [COMPONENT_ID_HEADER]: this.componentId,
        }),
        params: new HttpParams().set('name', sanitizeSimulationName(this.getSimulationName())),
      };

      this.loadingService.startLoading(this.componentId);

      this.simulationsService
        .addSimulation(directions, sanitizeFileName(this.fileName.getRawValue()), options, this.componentId)
        .pipe(this.takeUntilDestroyed())
        .subscribe(() => {
          this.snackbarService.openCustom({
            message: 'Simulation started successfully',
            title: 'Success',
            type: 'success',
            action: 'Close',
          });
          this.fileName.patchValue('');
          this.router.navigate(['/dashboard']);
        });
    }
  }

  directionSelected(): boolean {
    const selectedDirections: Directions = this.simulationDirectionsForm.getRawValue();
    return Object.values(selectedDirections).some(Boolean);
  }

  isSimulationInputValid(): boolean {
    return Boolean(
      this.fileName.getRawValue() &&
      this.simulationNameForm.valid &&
      this.directionSelected(),
    );
  }

  private getSimulationName(): string {
    const { name } = this.simulationNameForm.getRawValue();
    return name || (this.fileName.getRawValue() ?? '').split('.')[0];
  }

  private initSimulationNameForm(): void {
    this.simulationNameForm = this.fb.group({
      name: ['', Validators.maxLength(256)],
    });
  }

  private initSimulationDirectionsForm(): void {
    this.simulationDirectionsForm = this.fb.group({
      xDirection: [true],
      yDirection: [true],
      zDirection: [true],
    });
  }

  private initFileName(): void {
    this.fileName = new FormControl({ value: '', disabled: true });
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((loadingState: boolean) => {
        this.isLoading = loadingState;
      });
  }
}
